import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Home, LayoutDashboard, MessageSquare, Database, Building2, Store, Settings, LogOut, ChevronsLeft, ChevronsRight, Archive } from "lucide-react";
import oktogoLogo from "@/assets/oktogo-logo.png";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useStudio } from "@/contexts/StudioContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getDataSectionBasePath, isShortTermRentalVertical } from "@/lib/vertical";
import { applyStoredStudioForSurface } from "@/hooks/useSurfaceStudioScope";
import { userCanAccessArchiveHub } from "@/lib/archive-access";

interface AppSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export default function AppSidebar({ collapsed, onToggleCollapse }: AppSidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isAllStudios, activeStudioId, setActiveStudioId } = useStudio();
  const dataSectionPath = getDataSectionBasePath(user?.accountVertical);
  const isShopContext = isShortTermRentalVertical(user?.accountVertical);
  const dataNavLabel = isShortTermRentalVertical(user?.accountVertical) ? t("nav.propertyData") : t("nav.studioData");

  const canSeeArchive = useMemo(() => userCanAccessArchiveHub(user), [user]);

  const navItems = useMemo(() => {
    const base: Array<{
      label: string;
      icon: typeof Home;
      activeIcon?: typeof Home;
      path: string;
      requiresStudio?: true;
      /** UX-15: Archive hub — show nav row only when `userCanAccessArchiveHub` (Owner of ≥1 active studio). */
      requiresArchiveHubAccess?: true;
    }> = [
      { label: t("nav.dashboard"), icon: LayoutDashboard, activeIcon: LayoutDashboard, path: "/dashboard" },
      { label: t("nav.chats"), icon: MessageSquare, path: "/chats" },
      { label: dataNavLabel, icon: isShopContext ? Store : Building2, activeIcon: isShopContext ? Store : Building2, path: dataSectionPath, requiresStudio: true },
      { label: t("nav.archive"), icon: Archive, path: "/archive", requiresArchiveHubAccess: true },
      { label: t("nav.settings"), icon: Settings, path: "/settings" },
    ];
    return base.filter((item) => !item.requiresArchiveHubAccess || canSeeArchive);
  }, [t, dataNavLabel, dataSectionPath, canSeeArchive, isShopContext]);

  return (
    <aside className={cn(
      "fixed left-0 top-0 z-30 flex h-screen flex-col border-r border-black/[0.08] bg-[#FAFAFA] py-5 transition-all duration-200",
      collapsed ? "w-[64px] px-2" : "w-[240px] px-3"
    )}>
      {/* Logo + Collapse toggle */}
      <div
        className={cn(
          "mb-8 flex items-center",
          collapsed ? "flex-col justify-center gap-3" : "justify-between px-3",
        )}
      >
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Link
              to="/dashboard"
              aria-label={t("nav.goToDashboard")}
              className={cn(
                "shrink-0 rounded-md outline-none ring-offset-2 ring-offset-[#FAFAFA] transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary/40",
                collapsed && "flex justify-center",
              )}
            >
              <img
                src={collapsed ? "/favicon.svg" : oktogoLogo}
                alt="oktogo.ai"
                className={cn(
                  "shrink-0",
                  collapsed ? "h-[37.312px] w-[37.312px] object-contain" : "h-[42px] w-auto",
                )}
              />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">{t("nav.dashboard")}</TooltipContent>
        </Tooltip>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onToggleCollapse}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/60 hover:bg-muted/60 hover:text-foreground transition-colors"
            >
              {collapsed ? <ChevronsRight className="h-4 w-4" strokeWidth={1.5} /> : <ChevronsLeft className="h-4 w-4" strokeWidth={1.5} />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{collapsed ? t("nav.expandMenu") : t("nav.collapseMenu")}</TooltipContent>
        </Tooltip>
      </div>

      {/* Nav items — uniform row gap; icon & label share same vertical midline (leading matches icon box). */}
      <nav className="flex flex-1 flex-col gap-2">
        {navItems.map((item) => {
          const isActive =
            item.path === "/chats"
              ? location.pathname.startsWith("/chats")
              : item.path === "/archive"
                ? location.pathname === "/archive"
                : item.path === dataSectionPath
                  ? location.pathname.startsWith("/studio-data") || location.pathname.startsWith("/property-data")
                  : location.pathname.startsWith(item.path);
          const Icon = isActive && item.activeIcon ? item.activeIcon : item.icon;
          const needsStudioSelect = item.requiresStudio && isAllStudios;

          const handleClick = () => {
            if (needsStudioSelect && user?.studios?.[0]) {
              setActiveStudioId(user.studios[0].id);
            }
            const studios = user?.studios ?? [];
            if (studios.length > 0) {
              if (item.path === "/chats") {
                applyStoredStudioForSurface("chats", studios, activeStudioId, setActiveStudioId, { sync: true });
              } else if (item.path === "/dashboard") {
                applyStoredStudioForSurface("dashboard", studios, activeStudioId, setActiveStudioId, { sync: true });
              } else if (item.path === dataSectionPath) {
                applyStoredStudioForSurface("data", studios, activeStudioId, setActiveStudioId, { sync: true });
              }
            }
            navigate(item.path);
          };

          const prefetchChatsChunk = () => {
            if (item.path === "/chats") void import("@/pages/Chats");
          };

          const button = (
            <button
              key={item.path}
              type="button"
              onPointerEnter={prefetchChatsChunk}
              onClick={handleClick}
              className={cn(
                "flex min-h-10 items-center rounded-xl transition-colors duration-150",
                collapsed ? "mx-auto h-10 w-10 shrink-0 justify-center" : "h-10 w-full gap-3 px-3 text-[13px] font-medium leading-5",
                isActive
                  ? "bg-primary/10 font-semibold text-primary"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )}
            >
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </span>
              {!collapsed && <span className="min-w-0 truncate leading-5">{item.label}</span>}
            </button>
          );

          if (collapsed) {
            return (
              <Tooltip key={item.path} delayDuration={100}>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            );
          }

          return button;
        })}
      </nav>

      {/* Footer */}
      <div className={cn("border-t border-border/50 pt-4", collapsed ? "px-0" : "px-3")}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted font-body text-sm font-semibold text-muted-foreground">
                  {user?.name?.charAt(0) ?? "U"}
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">{user?.name}</TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={logout}
                  aria-label={t("nav.logOut")}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("nav.logOut")}</TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted font-body text-sm font-semibold text-muted-foreground">
              {user?.name?.charAt(0) ?? "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">{user?.name}</p>
              <p className="truncate text-[11px] text-muted-foreground">{user?.email}</p>
            </div>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={logout}
                  aria-label={t("nav.logOut")}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("nav.logOut")}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </aside>
  );
}
